import { useCallback, useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useSearchParams } from 'react-router';
import type { LoginMethod } from '@linktivity/ars-neo';
import { Link } from '@linktivity/link-ui';
import { useProfileGuardNavigate } from '@link/share/hooks';
import { BaseAuth } from '@link/share/stores';
import {
  buildLocalizedPath,
  readRedirectParam,
  type ProfileGuardOptions
} from '@link/share/utils';
import ShareAuth from '@link/share/vendors/firebase';
import LoginMethods from './LoginMethods';
import Signin from './Signin';
import Signup from './Signup';
import styles from './login.module.css';

interface LoginProps {
  auth: BaseAuth;
  loggedIn?: boolean;
  firebase: ShareAuth;
  loginMethods?: LoginMethod[];
  profileGuard?: ProfileGuardOptions;
  onAuthSuccess?: (
    redirectTo: string,
    isNewUser: boolean
  ) => void | Promise<void>;
}

const LOGIN_STEPS = {
  INITIAL: 'initial',
  SIGNIN: 'signin',
  SIGNUP: 'signup'
};

const titleMap = {
  [LOGIN_STEPS.INITIAL]: 'share.login.title',
  [LOGIN_STEPS.SIGNIN]: 'share.login.button',
  [LOGIN_STEPS.SIGNUP]: 'share.login.newAccount'
};

export const Login: React.FC<LoginProps> = ({
  auth,
  loggedIn,
  firebase,
  loginMethods,
  profileGuard,
  onAuthSuccess
}) => {
  const { t } = useTranslation();
  const guardedNavigate = useProfileGuardNavigate(profileGuard);
  const [searchParams] = useSearchParams();

  const fromPath = readRedirectParam(searchParams) ?? '/';

  const [step, setStep] = useState<string>(LOGIN_STEPS.INITIAL);
  const [sharedEmail, setSharedEmail] = useState<string>('');

  useEffect(() => {
    if (loggedIn) {
      guardedNavigate(fromPath);
    }
  }, [loggedIn, fromPath, guardedNavigate]);

  const handleEmailSubmit = useCallback((email: string, nextStep: string) => {
    setSharedEmail(email);
    setStep(nextStep);
  }, []);

  const handleBack = useCallback(() => {
    setStep(LOGIN_STEPS.INITIAL);
  }, []);

  const signinSuccessHandler = useCallback(
    () => onAuthSuccess?.(fromPath, false),
    [onAuthSuccess, fromPath]
  );

  const signupSuccessHandler = useCallback(
    () => onAuthSuccess?.(fromPath, true),
    [onAuthSuccess, fromPath]
  );

  return (
    <main className={styles.login}>
      <article className={styles.inner}>
        <div className={styles.content}>
          <h1 className={styles.title}>{t(titleMap[step])}</h1>
          {step === LOGIN_STEPS.INITIAL && (
            <p className={styles.caption}>{t('share.login.caption')}</p>
          )}
        </div>
        {step === LOGIN_STEPS.INITIAL && (
          <LoginMethods
            onSubmit={handleEmailSubmit}
            firebase={firebase}
            auth={auth}
            loginMethods={loginMethods}
          />
        )}
        {step === LOGIN_STEPS.SIGNIN && (
          <Signin
            initialEmail={sharedEmail}
            firebase={firebase}
            auth={auth}
            onBack={handleBack}
            onAuthSuccess={onAuthSuccess ? signinSuccessHandler : undefined}
          />
        )}
        {step === LOGIN_STEPS.SIGNUP && (
          <Signup
            initialEmail={sharedEmail}
            firebase={firebase}
            auth={auth}
            onBack={handleBack}
            onAuthSuccess={onAuthSuccess ? signupSuccessHandler : undefined}
          />
        )}
        <div className={styles.content}>
          <p className={styles.terms}>
            <Trans
              i18nKey="share.login.signInAgree"
              t={t}
              values={{
                terms: t('views.terms.title'),
                privacy: t('views.privacy.title')
              }}
              components={{
                TermsLink: (
                  <Link
                    target="_blank"
                    className={styles.link}
                    to={buildLocalizedPath('terms')}
                  />
                ),
                PrivacyLink: (
                  <Link
                    target="_blank"
                    className={styles.link}
                    to={buildLocalizedPath('privacy')}
                  />
                )
              }}
            />
          </p>
        </div>
      </article>
    </main>
  );
};

export default Login;
